import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, CopyText } from '@/components';
import { useAppSelector } from '@/redux/hooks';
import { amlTraceSelectors } from '@/redux/selectors';
import { ChainsDictionary } from '@/config';
import { getIconByNetwork } from '@/helpers';
import useLocateTraceByUuid from '../TraceInvestigation/hooks/useLocateTraceByUuid';
import useTraceStatus from '../TraceInvestigation/hooks/useTraceStatus';
import { cleanVisualizationUrl } from '../TraceInvestigation/helpers';
import { TraceHistoryMessages } from '../TraceInvestigation/components/TraceHistoryMessages/TraceHistoryMessages';

import styles from './TraceInvestigationDetails.module.sass';

const VISIBLE_TX_COUNT = 2;

const TraceInvestigationDetails = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const uuid = location?.state?.uuid;

  const [isTxListExpanded, setIsTxListExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [uuid]);

  const { item, notFound } = useLocateTraceByUuid(uuid);
  const traceUpdates = useAppSelector((state) =>
    amlTraceSelectors.getTraceById(state, uuid || ''),
  );
  const { visualizationUrl, statusMessages, errorMessages, hasVisualization } =
    useTraceStatus(item, traceUpdates);

  const hasErrors = errorMessages.length > 0;

  const txList = item?.txList || [];
  const hasHiddenTxs = txList.length > VISIBLE_TX_COUNT;
  const visibleTxList = isTxListExpanded
    ? txList
    : txList.slice(0, VISIBLE_TX_COUNT);

  return (
    <>
      {notFound && (
        <div className={styles.notFound}>
          {t('translation.ai_investigation_not_found')}
        </div>
      )}

      {!notFound && hasErrors && !hasVisualization && (
        <TraceHistoryMessages messages={errorMessages} />
      )}

      {!notFound && !hasErrors && !hasVisualization && (
        <TraceHistoryMessages messages={statusMessages} />
      )}

      {hasVisualization && visualizationUrl && (
        <>
          {visibleTxList.map((tx, index) => (
            <div
              key={`${tx.hash || tx.address}-${index}`}
              className={styles.summary}
            >
              <img
                src={getIconByNetwork(tx.blockchain)}
                alt={tx.blockchain}
                className={styles.summaryIcon}
              />
              <div className={styles.summaryInfo}>
                <CopyText
                  text={tx.hash || tx.address || ''}
                  copyText={tx.hash || tx.address || ''}
                  className={styles.summaryHash}
                />
                <div className={styles.summaryMeta}>
                  <span className={styles.summaryNetwork}>
                    {ChainsDictionary[tx.blockchain]?.name || tx.blockchain}
                  </span>
                  <span className={styles.summaryAsset}>{tx.asset}</span>
                </div>
              </div>
            </div>
          ))}

          {hasHiddenTxs && (
            <div className={styles.txListToggle}>
              <Button
                corner="rect"
                size="s"
                type="bordered"
                onClick={() => setIsTxListExpanded((prev) => !prev)}
              >
                {isTxListExpanded
                  ? t('translation.show_less')
                  : t('translation.show_more')}
              </Button>
            </div>
          )}

          <div className={styles.visualization}>
            <iframe
              src={cleanVisualizationUrl(visualizationUrl)}
              title={`Trace visualization ${uuid}`}
              allow="clipboard-read; clipboard-write"
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-popups allow-modals allow-forms allow-downloads"
            />
          </div>
        </>
      )}
    </>
  );
};

export default TraceInvestigationDetails;
